/**
 * InvestmentDetail — detail view for an investment account.
 * Sub-tabs: Overview · Holdings · Activity · Statements · Performance.
 * Overview + Holdings are fully designed; the rest are placeholders.
 */
import React, { useState } from 'react';
import { Clock } from 'lucide-react';
import DetailHeader from './DetailHeader';
import SubTabs from './SubTabs';
import { formatMoney } from '@/lib/format';
import { ACCOUNTS_TONE } from '@/lib/accountsDemo';

/**
 * "Last priced · Jul 25, 10:11" chip. Colour-codes by staleness:
 *   • < 24 h   → emerald (fresh)
 *   • 1-7 d    → amber   (aging)
 *   • > 7 d    → muted   (stale — user should refresh)
 * Manually-priced holdings (auto_update=false) render a "Manual price"
 * chip instead so the user knows Yahoo isn't the source of truth.
 */
function LastPricedChip({ lastPricedAt, autoUpdate }) {
  if (!autoUpdate) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground/80 mt-0.5"
        data-testid="last-priced-manual"
      >
        <Clock className="h-2.5 w-2.5" />
        Manual price
      </span>
    );
  }
  if (!lastPricedAt) return null;
  let d; try { d = new Date(lastPricedAt); } catch { return null; }
  if (!(d instanceof Date) || isNaN(d.getTime())) return null;
  const ageMs = Date.now() - d.getTime();
  const ageDays = ageMs / (1000 * 60 * 60 * 24);
  const cls =
    ageDays < 1 ? 'text-emerald-600'
    : ageDays < 7 ? 'text-amber-600'
    : 'text-muted-foreground/70';
  const label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    + ', ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium mt-0.5 ${cls}`}
      data-testid="last-priced-chip"
      title={`Last priced from data source at ${d.toLocaleString()}`}
    >
      <Clock className="h-2.5 w-2.5" />
      Last priced · {label}
    </span>
  );
}

function StubTab({ label }) {
  return (
    <div className="bg-card rounded-xl border border-border px-6 py-12 text-center shadow-sm" data-testid={`accounts-inv-stub-${label.toLowerCase()}`}>
      <p className="text-sm font-medium text-charcoal mb-1">{label}</p>
      <p className="text-xs text-muted-foreground">This section is coming soon.</p>
    </div>
  );
}

export default function InvestmentDetail({ account, onBack, baseCurrency = 'INR', onEdit, onDelete, onSetBalance }) {
  const [tab, setTab] = useState('overview');
  const isDeposit = account.raw?.kind === 'deposit' || account.kind === 'deposit' || account.type === 'other-investment' && account.raw?.kind === 'deposit';
  // Deposits (FDs/RDs) don't have per-security holdings or activity —
  // they're a single instrument with contribution + maturity fields, so
  // the Holdings and Activity sub-tabs are dropped for them.
  const tabs = isDeposit
    ? [
        { id: 'overview',    label: 'Overview' },
        { id: 'statements',  label: 'Statements' },
        { id: 'performance', label: 'Performance' },
      ]
    : [
        { id: 'overview',    label: 'Overview' },
        { id: 'holdings',    label: 'Holdings' },
        { id: 'activity',    label: 'Activity' },
        { id: 'statements',  label: 'Statements' },
        { id: 'performance', label: 'Performance' },
      ];

  const holdings = Array.isArray(account.holdings) ? account.holdings : [];
  const totalReturn = holdings.reduce((s, h) => s + (h.value * (h.return_pct || 0)) / 100, 0);

  return (
    <div data-testid="accounts-investment-detail">
      <DetailHeader
        account={account}
        onBack={onBack}
        balance={formatMoney(Math.abs(account.balance || 0), baseCurrency)}
        balanceLabel="Current value"
        balanceColor={ACCOUNTS_TONE.invested}
        onEdit={onEdit}
        onDelete={onDelete}
        onSetBalance={onSetBalance}
      />
      <SubTabs tabs={tabs} active={tab} onChange={setTab} testidPrefix="accounts-inv-tab" />

      {tab === 'overview' && (
        <div className="space-y-5">
          {isDeposit ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {(() => {
                const raw = account.raw || {};
                const rate = raw.interest_rate;
                const principal = raw.principal_amount ?? raw.monthly_contribution;
                const maturity = raw.maturity_date;
                const subtype = (raw.deposit_subtype || '').toUpperCase();
                const fmtD = (iso) => {
                  if (!iso) return '—';
                  try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); }
                  catch { return iso; }
                };
                const metrics = [
                  { label: subtype === 'RD' ? 'Monthly contribution' : 'Principal',
                    value: principal ? formatMoney(principal, baseCurrency) : '—',
                    color: '#1E1E1E' },
                  { label: 'Interest rate',
                    value: rate ? `${rate}% p.a.` : '—',
                    color: '#1E1E1E' },
                  { label: 'Maturity',
                    value: fmtD(maturity),
                    color: '#1E1E1E' },
                  { label: 'Subtype',
                    value: subtype || '—',
                    color: '#1E1E1E' },
                ];
                return metrics.map((m) => (
                  <div key={m.label} className="bg-card rounded-xl border border-border px-4 py-4 shadow-sm">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-2">{m.label}</p>
                    <p className="num font-display text-lg font-medium" style={{ color: m.color }}>{m.value}</p>
                  </div>
                ));
              })()}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  label: "Today's change",
                  value: account.day_change != null
                    ? `${account.day_change > 0 ? '+' : '−'}${formatMoney(Math.abs(account.day_change), baseCurrency)}`
                    : '—',
                  color: (account.day_change ?? 0) >= 0 ? ACCOUNTS_TONE.positive : ACCOUNTS_TONE.liability,
                },
                { label: 'Total return', value: `+${formatMoney(Math.round(totalReturn), baseCurrency)}`, color: ACCOUNTS_TONE.positive },
                { label: 'Holdings',     value: String(holdings.length), color: '#1E1E1E' },
              ].map((m) => (
                <div key={m.label} className="bg-card rounded-xl border border-border px-4 py-4 shadow-sm">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-2">{m.label}</p>
                  <p className="num font-display text-xl font-medium" style={{ color: m.color }}>{m.value}</p>
                </div>
              ))}
            </div>
          )}

          {!isDeposit && holdings.length > 0 && (
            <section className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
              <header className="px-5 py-3.5 border-b border-border/60 bg-offwhite/60">
                <p className="text-xs font-semibold text-charcoal">Top Holdings</p>
              </header>
              <ul className="divide-y divide-border/40">
                {holdings.slice(0, 3).map((h, i) => (
                  <li key={i} className="flex items-center gap-4 px-5 py-3.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-charcoal truncate">{h.name}</p>
                      {h.ticker && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {h.ticker} · {h.units} {h.type === 'stock' ? 'shares' : 'units'}
                        </p>
                      )}
                      <LastPricedChip lastPricedAt={h.last_priced_at} autoUpdate={h.auto_update} />
                    </div>
                    <div className="text-right shrink-0">
                      <p className="num text-sm font-semibold text-charcoal">{formatMoney(h.value, baseCurrency)}</p>
                      <p
                        className="text-[11px] font-medium mt-0.5"
                        style={{ color: h.return_pct >= 0 ? ACCOUNTS_TONE.positive : ACCOUNTS_TONE.liability }}
                      >
                        {h.return_pct >= 0 ? '+' : ''}{h.return_pct}%
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {tab === 'holdings' && holdings.length > 0 && (
        <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
          <div className="hidden sm:grid grid-cols-5 gap-4 px-5 py-3 border-b border-border/60 bg-offwhite/60">
            {['Name', 'Units / Shares', 'Price', 'Value', 'Return'].map((h) => (
              <p key={h} className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">{h}</p>
            ))}
          </div>
          <ul className="divide-y divide-border/40">
            {holdings.map((h, i) => (
              <li key={i} className="grid grid-cols-2 sm:grid-cols-5 gap-4 px-5 py-4 items-center">
                <div className="col-span-2 sm:col-span-1 min-w-0">
                  <p className="text-sm font-medium text-charcoal truncate">{h.name}</p>
                  {h.ticker && <p className="text-[11px] text-muted-foreground">{h.ticker}</p>}
                  <LastPricedChip lastPricedAt={h.last_priced_at} autoUpdate={h.auto_update} />
                </div>
                <p className="num text-sm text-muted-foreground hidden sm:block">{h.units}</p>
                <p className="num text-sm text-muted-foreground hidden sm:block">{formatMoney(h.price, baseCurrency)}</p>
                <p className="num text-sm font-medium text-charcoal">{formatMoney(h.value, baseCurrency)}</p>
                <p
                  className="num text-sm font-medium"
                  style={{ color: h.return_pct >= 0 ? ACCOUNTS_TONE.positive : ACCOUNTS_TONE.liability }}
                >
                  {h.return_pct >= 0 ? '+' : ''}{h.return_pct}%
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === 'holdings' && holdings.length === 0 && <StubTab label="Holdings" />}
      {tab === 'activity'    && <StubTab label="Activity" />}
      {tab === 'statements'  && <StubTab label="Statements" />}
      {tab === 'performance' && <StubTab label="Performance" />}
    </div>
  );
}
